import React, { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft } from 'react-icons/fa';
import { AuthContext } from './Provider';
import OptionMenu from './MenuBar';
import './NativeTopBar.css';

/**
 * Native (Capacitor) app bar. Replaces the desktop-web header inside the app:
 * - top-level tabs (Home): WayShare wordmark on the left
 * - sub-pages: a back arrow + the page title
 * The account menu (profile / settings / logout / language) stays on the right
 * so it's always reachable; notifications live in the bottom nav.
 * Rendered only on native (see App.js); web keeps the full <Header/>.
 */
const TOP_LEVEL = ['/', '/home'];

// pathname prefix → [i18n key | null, English fallback]
const TITLES = [
  ['/settings', 'navigation.settings', 'Settings'],
  ['/myprofileform', 'navigation.profile', 'Profile'],
  ['/activity', 'menu.activity', 'Activity'],
  ['/publishpost', null, 'Offer a ride'],
  ['/sendpackage', null, 'Send a package'],
  ['/post/', null, 'Ride details'],
  ['/package/', null, 'Package details'],
  ['/request/', null, 'Request'],
  ['/about', 'navigation.about', 'About'],
  ['/contact', 'navigation.contact', 'Contact'],
  ['/benefits', null, 'Benefits'],
  ['/events', 'navigation.events', 'Events'],
  ['/groups', 'navigation.groups', 'Groups'],
  ['/privacy', null, 'Privacy Policy'],
  ['/terms', null, 'Terms of Service'],
  ['/safety', null, 'Safety'],
  ['/cookies', null, 'Cookie Policy'],
  ['/developer', null, 'Developer'],
  ['/subscription', null, 'Plans'],
];

function titleFor(pathname, t) {
  const p = (pathname || '').toLowerCase();
  for (const [prefix, key, fallback] of TITLES) {
    if (p === prefix || p.startsWith(prefix)) {
      return key ? t(key, { defaultValue: fallback }) : fallback;
    }
  }
  return '';
}

function NativeTopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);

  const isTopLevel = TOP_LEVEL.includes((location.pathname || '').toLowerCase());
  const title = isTopLevel ? '' : titleFor(location.pathname, t);

  return (
    <header className="native-topbar">
      <div className="native-topbar-inner">
        <div className="native-topbar-left">
          {isTopLevel ? (
            <div
              className="native-topbar-logo"
              onClick={() => navigate('/home')}
              role="button"
              tabIndex={0}
            >
              Way<span>Share</span>
            </div>
          ) : (
            <>
              <button
                type="button"
                className="native-topbar-back"
                onClick={() => navigate(-1)}
                aria-label={t('buttons.back', { defaultValue: 'Back' })}
              >
                <FaArrowLeft />
              </button>
              <span className="native-topbar-title">{title}</span>
            </>
          )}
        </div>

        <div className="native-topbar-right">
          {user && <OptionMenu />}
        </div>
      </div>
    </header>
  );
}

export default NativeTopBar;
